import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, TestTube, Activity, Droplet, Sparkles, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import { SiteNav } from '../components/SiteNav';
import { pillars } from '../data/pillars';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_rejuven-health/artifacts/rqq06olv_Rejuven%20Logo%20-%20Website.png';

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const microbiomeKits = [
  { icon: TestTube, title: 'Gut Intelligence', desc: 'Viome gut microbiome test to understand digestion, inflammation and food response.' },
  { icon: Activity, title: 'Oral Health', desc: 'Viome oral microbiome test to assess the bacteria shaping oral and whole-body health.' },
  { icon: Droplet, title: 'Blood Insights', desc: 'Viome blood-based biomarkers to round out a complete picture of your biology.' },
];

const microbiomeIncludes = [
  'Three at-home Viome testing kits (gut, oral & blood)',
  'A full microbiome analysis interpreted for your goals',
  'Personalized, preventative nutrition & lifestyle guidance',
  'Educational insights — not medical diagnosis or treatment',
];

const programIncludes = [
  'A plan built around your chosen focus area (pillar)',
  'Genotype & microbiome-informed nutrition guidance',
  'Lifestyle, sleep, movement & stress recommendations',
  'Ongoing adjustments as your biology and goals evolve',
  'Access to the Rejuven wellness companion app',
];

export default function ServicesPage() {
  return (
    <div className="min-h-screen bg-[#FAF8F7] text-[#2B2625]">
      <SiteNav />

      {/* Hero */}
      <section className="py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute -top-24 right-0 w-[460px] h-[460px] bg-[#E8D5D1]/50 rounded-full blur-[130px]" />
        <div className="max-w-5xl mx-auto px-6 lg:px-8 relative text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
            <p className="text-[#C28B87] text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase mb-5">Our Services</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2B2625] mb-6 font-medium leading-[1.05]">Precision wellness, delivered two ways</h1>
            <p className="text-lg lg:text-xl text-[#6C6361] leading-relaxed max-w-2xl mx-auto">From complete microbiome testing to an ongoing personalized program, choose the path that fits where you are on your wellness journey.</p>
          </motion.div>
        </div>
      </section>

      {/* Service 1: Microbiome Package */}
      <section className="py-12 lg:py-16 px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div className="grid lg:grid-cols-2 gap-12 items-center" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} data-testid="service-microbiome">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#F3EFEF] rounded-full text-[#C28B87] text-sm font-semibold mb-5"><TestTube className="w-4 h-4" /> Microbiome Package</div>
              <h2 className="font-display text-3xl lg:text-4xl text-[#2B2625] mb-4 font-medium">A complete look inside your biology</h2>
              <p className="text-[#6C6361] leading-relaxed mb-6">A full microbiome analysis powered by Viome&apos;s gut, oral and blood testing kits. We turn your results into clear, personalized and preventative guidance — so you finally understand what your body is telling you.</p>
              <ul className="space-y-3 mb-8">
                {microbiomeIncludes.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-[#6C6361]"><span className="w-6 h-6 rounded-full bg-[#E8D5D1] flex items-center justify-center flex-shrink-0 mt-0.5"><Check className="w-3.5 h-3.5 text-[#C28B87]" /></span>{item}</li>
                ))}
              </ul>
              <Link to="/services/microbiome-package"><Button className="bg-[#C28B87] text-white hover:bg-[#b07b77] rounded-full px-8 py-6 text-base font-medium shadow-[0_10px_30px_rgba(194,139,135,0.3)] transition-transform hover:scale-[1.02]" data-testid="microbiome-cta">View Package <ArrowRight className="ml-2 w-5 h-5" /></Button></Link>
            </div>
            <div className="grid gap-4">
              {microbiomeKits.map((k, idx) => (
                <motion.div key={k.title} className="bg-[#FAF8F7] rounded-3xl p-6 border border-[#E8D5D1]/70 flex items-start gap-4" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.08 }}>
                  <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center flex-shrink-0"><k.icon className="w-6 h-6 text-[#C28B87]" strokeWidth={1.7} /></div>
                  <div><h3 className="font-display text-xl text-[#2B2625] font-medium">{k.title}</h3><p className="text-[#6C6361] text-sm leading-relaxed mt-1">{k.desc}</p></div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Service 2: Personalized Program */}
      <section className="py-12 lg:py-16 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div className="bg-[#2B2625] text-white rounded-[2.5rem] p-8 lg:p-14 relative overflow-hidden" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} data-testid="service-program">
            <div className="absolute -top-16 -right-16 w-72 h-72 bg-[#C28B87]/25 rounded-full blur-[120px]" />
            <div className="relative grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-[#E8D5D1] text-sm font-semibold mb-5"><Sparkles className="w-4 h-4" /> Personalized Program</div>
                <h2 className="font-display text-3xl lg:text-4xl mb-4 font-medium">Personalized Longevity &amp; Wellness Program</h2>
                <p className="text-white/70 leading-relaxed mb-6">An ongoing, personalized program built around the focus area that matters most to you. Choose your pillar — from fertility to menopause to longevity — and receive preventative guidance that evolves with your biology.</p>
                <ul className="space-y-3 mb-8">
                  {programIncludes.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-white/80"><span className="w-6 h-6 rounded-full bg-[#C28B87]/40 flex items-center justify-center flex-shrink-0 mt-0.5"><Check className="w-3.5 h-3.5 text-[#E8D5D1]" /></span>{item}</li>
                  ))}
                </ul>
                <Link to="/services/longevity-program"><Button className="bg-white text-[#2B2625] hover:bg-[#F3EFEF] rounded-full px-8 py-6 text-base font-semibold transition-transform hover:scale-[1.02]" data-testid="program-cta">View Program <ArrowRight className="ml-2 w-5 h-5" /></Button></Link>
              </div>
              <div>
                <p className="text-[#E8D5D1] text-xs font-semibold tracking-[0.2em] uppercase mb-4">Choose your focus</p>
                <div className="grid grid-cols-2 gap-3">
                  {pillars.map((p) => {
                    const Icon = p.icon;
                    return (
                      <Link key={p.slug} to={`/pillars/${p.slug}`} className="group flex items-center gap-3 bg-white/10 hover:bg-white/15 rounded-2xl p-3 transition-colors" data-testid={`program-pillar-${p.slug}`}>
                        <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0"><Icon className="w-5 h-5 text-[#E8D5D1]" strokeWidth={1.7} /></div>
                        <span className="text-sm text-white/90 font-medium leading-tight">{p.title}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Disclaimer note */}
      <section className="px-6 lg:px-8 pb-8">
        <div className="max-w-4xl mx-auto bg-[#F3EFEF] rounded-2xl p-5 text-center">
          <p className="text-xs text-[#6C6361] leading-relaxed">Viome is a third-party testing provider. Rejuven facilitates testing and interprets results for educational, preventative wellness only — this is not medical diagnosis or treatment. Always consult a licensed healthcare provider for medical decisions.</p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 lg:px-12 bg-gradient-to-b from-[#FAF8F7] to-[#F3EFEF]/50">
        <motion.div className="max-w-3xl mx-auto text-center" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <h2 className="font-display text-3xl sm:text-4xl text-[#2B2625] mb-5 font-medium">Not sure where to start?</h2>
          <p className="text-[#6C6361] mb-8 leading-relaxed">Join the private beta and we&apos;ll help you choose the right path for your biology and goals.</p>
          <Link to="/?waitlist=true"><Button className="bg-[#C28B87] text-white hover:bg-[#b07b77] rounded-full px-10 py-7 text-lg font-medium shadow-[0_10px_30px_rgba(194,139,135,0.3)] transition-transform hover:scale-[1.02]">Join the Waitlist <ChevronRight className="ml-1 w-5 h-5" /></Button></Link>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="bg-[#221D1C] text-white py-14 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 mb-8">
            <div className="bg-white/10 rounded-2xl px-6 py-3"><img src={LOGO_URL} alt="Rejuven" className="h-8" /></div>
            <div className="flex items-center gap-8 text-sm text-white/55">
              <Link to="/about" className="hover:text-white transition-colors">About</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            </div>
            <p className="text-white/35 text-sm">© 2026 Rejuven Inc.</p>
          </div>
          <div className="border-t border-white/10 pt-6">
            <p className="text-white/45 text-xs leading-relaxed max-w-4xl mx-auto text-center"><strong className="text-white/60">Medical disclaimer:</strong> Rejuven provides educational and preventative wellness information only. It is not medical advice and is not a substitute for professional diagnosis, treatment or the advice of your licensed physician. We do not diagnose, treat, cure or prevent any disease, and do not prescribe medications, hormones or peptides. Always consult a qualified healthcare provider before making changes to your health regimen.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
